import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom'; 
import axiosClient from '../../api/axiosClient';
import { Lock, ShieldCheck, Loader2, ArrowRight, KeyRound } from 'lucide-react';

const LoginPage = () => {
  const [step, setStep] = useState(1); // 1 = Creds, 2 = OTP
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [tempToken, setTempToken] = useState(null); // Holds pre-auth token
  
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { loginWithToken } = useAuth(); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper to handle post-login redirect
  const handleSuccessRedirect = (token) => {
      // Check if there was a purchase intent
      const planIntent = searchParams.get('plan');
      
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        if (payload.role === 'customer') {
            // Redirect to Checkout if plan exists, else Dashboard
            if (planIntent) {
                navigate(`/portal/shop?plan=${planIntent}`);
            } else {
                navigate('/portal/dashboard');
            }
        } else {
            navigate('/'); // Admin
        }
      } catch {
        navigate('/');
      }
  };

  // --- Step 1: Submit Credentials ---
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const res = await axiosClient.post('/auth/token', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = res?.data || res;

      if (data.require_2fa) {
        setTempToken(data.access_token);
        setStep(2);
        return;
      }

      await loginWithToken(data.access_token);
      handleSuccessRedirect(data.access_token); 

    } catch (err) {
      console.error('Login error:', err);
      setError(err?.response?.data?.detail || err?.message || 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Step 2: Submit OTP ---
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const res = await axiosClient.post('/auth/verify-2fa', { otp }, {
          headers: { Authorization: `Bearer ${tempToken}` }
      });

      const data = res?.data || res;
      await loginWithToken(data.access_token);
      handleSuccessRedirect(data.access_token); 

    } catch (err) {
      console.error('2FA verification failed:', err);
      setError(err?.response?.data?.detail || 'Invalid OTP code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

      <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-slate-700 relative z-10">
        
        {/* Header */}
        <div className="p-8 text-center border-b border-slate-700/50">
          <div className="mx-auto bg-gradient-to-tr from-blue-500 to-indigo-600 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-4">
            {step === 1 ? <ShieldCheck className="w-8 h-8 text-white" /> : <KeyRound className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">AIRS Security</h1>
          <p className="text-slate-400 mt-2 text-sm">
            {step === 1 ? 'Enter your credentials to access the grid.' : 'Two-Factor Authentication Required.'}
          </p>
        </div>

        {/* Form Container */}
        <div className="p-8">
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl flex items-center">
              <span className="mr-2">⚠️</span> {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleCredentialsSubmit} className="space-y-5 animate-in fade-in slide-in-from-left-8 duration-300">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Email Identity</label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-slate-600 transition-all outline-none"
                  placeholder="admin@airs-security.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Passcode</label>
                <div className="relative">
                  <input
                    type="password"
                    required
                    className="w-full px-4 py-3 pl-11 bg-slate-900/50 border border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-slate-600 transition-all outline-none"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Lock className="w-5 h-5 text-slate-500 absolute left-3 top-3.5" />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center group"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <> Authenticate <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /> </>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1 text-center">Google Authenticator Code</label>
                <input
                  type="text"
                  required
                  maxLength={6}
                  autoFocus
                  className="w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-4 bg-slate-900/50 border border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-slate-700 transition-all outline-none"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Access'}
              </button>
              <button 
                type="button" 
                onClick={() => setStep(1)} 
                className="w-full text-sm text-slate-500 hover:text-slate-300 text-center mt-4"
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;